import React, {
  createContext,
  useContext,
  useCallback,
  ReactNode,
} from "react";
import { Device } from "../../../Types";
import firebaseService from "../../../firebase_service/firebaseService";
import useDeviceModal from "../hooks/useDeviceModal";

interface DeviceContextValue {
  device: Device;
  isLoading: boolean;
  refetch: () => void;
  updateConfig: <T>(field: string, value: T) => Promise<void>;
  updateField: <T>(field: string, value: T) => Promise<void>;
  currentKlydo: string;
  gift: ReturnType<typeof useDeviceModal>["gift"];
  timeOffset: number;
  hideDev: boolean;
  schedualOn: boolean;
  deleteGift: () => Promise<void>;
  detachClock: () => Promise<void>;
  handleRef: () => string;
  resetDeviceData: () => Promise<void>;
  removeRegister: () => Promise<void>;
}

const DeviceContext = createContext<DeviceContextValue | null>(null);

export const useDevice = (): DeviceContextValue => {
  const context = useContext(DeviceContext);
  if (!context) {
    throw new Error("useDevice must be used within a DeviceProvider");
  }
  return context;
};

interface DeviceProviderProps {
  deviceId: string;
  children: ReactNode;
  onClose?: () => void;
}

export const DeviceProvider: React.FC<DeviceProviderProps> = ({
  deviceId,
  children,
  onClose,
}) => {
  const {
    data,
    isLoading,
    refetch,
    gift,
    deleteGift,
    detachClock,
    currentKlydo,
    timeOffset,
    hideDev,
    schedualOn,
    handleRef,
  } = useDeviceModal(deviceId);

  const updateConfig = useCallback(
    async <T,>(field: string, value: T): Promise<void> => {
      if (!data) return;
      await firebaseService.updateConfig(data.id, field, value);
      refetch();
    },
    [data, refetch],
  );

  const updateField = useCallback(
    async <T,>(field: string, value: T): Promise<void> => {
      if (!data) return;
      await firebaseService.updateField(data.id, field, value);
      refetch();
    },
    [data, refetch],
  );

  const resetDeviceData = useCallback(async (): Promise<void> => {
    if (!data) return;
    await firebaseService.resetDeviceData(data.id, data.idf);
    refetch();
  }, [data, refetch]);

  const removeRegister = useCallback(async (): Promise<void> => {
    if (!data) return;
    await firebaseService.reomveRegister(data.id);
    refetch();
  }, [data, refetch]);

  const handleDeleteGift = useCallback(async (): Promise<void> => {
    await deleteGift();
    refetch();
  }, [deleteGift, refetch]);

  const handleDetachClock = useCallback(async (): Promise<void> => {
    await detachClock();
    refetch();
  }, [detachClock, refetch]);

  if (!data || isLoading) {
    return null;
  }

  return (
    <DeviceContext.Provider
      value={{
        device: data,
        isLoading,
        refetch,
        updateConfig,
        updateField,
        currentKlydo,
        gift,
        timeOffset,
        hideDev,
        schedualOn,
        deleteGift: handleDeleteGift,
        detachClock: handleDetachClock,
        handleRef,
        resetDeviceData,
        removeRegister,
      }}
    >
      {children}
    </DeviceContext.Provider>
  );
};
